/**
 * Shared guards for document-level keyboard shortcuts: the workspace's
 * single-key ones (`A` accept, `?` shortcuts dialog, `=` value bar) and the
 * app-wide chords (Cmd/Ctrl+B sidebar, Cmd/Ctrl+K palette, Cmd/Ctrl+Shift+F
 * Sean). The PDF viewer asks the same of focus before it takes it on mount.
 *
 * A Radix menu keeps focus inside `role="menu"` and consumes letter keys for
 * typeahead, so a shortcut must stay quiet while a menu is open: otherwise
 * one keystroke would both typeahead the menu and act on the focused cell.
 */
export function isWithinMenu(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('[role="menu"]') !== null;
}

/**
 * Whether the key belongs to the field the user is typing in: a form control,
 * a contenteditable region, or an ARIA text, search or combobox widget. A
 * shortcut stays quiet there so typing a character, or a chord such as
 * Cmd/Ctrl+B for bold, never also acts on the page.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    return true;
  }
  if (target.isContentEditable) return true;
  const role = target.getAttribute('role');
  return role === 'textbox' || role === 'combobox' || role === 'searchbox';
}

/** Input types that take typed text; the others are buttons, toggles, sliders and pickers. */
const TEXT_ENTRY_INPUT_TYPES: ReadonlySet<string> = new Set([
  'text',
  'search',
  'email',
  'url',
  'tel',
  'password',
  'number',
  'date',
  'datetime-local',
  'month',
  'time',
  'week',
]);

/**
 * Whether the user is typing text into the target: like `isEditableTarget`,
 * except that an input counts only when it takes text, not a checkbox, radio,
 * button, range or other control a keystroke cannot type into.
 */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  if (target instanceof HTMLInputElement) return TEXT_ENTRY_INPUT_TYPES.has(target.type);
  return isEditableTarget(target);
}
